/**
 * Real-size PDF export (jsPDF + svg2pdf.js): vector graphics at true mm scale.
 * A4 tiling: one global poster grid; optional lineRegionsMm skips tiles that only cover whitespace
 * (e.g. between a left vertical column and a right horizontal block — no duplicate passes per line).
 */
import { jsPDF } from 'jspdf';
import 'svg2pdf.js';

/** CSS px per mm at 96dpi (matches OpentypeCanvas export). */
const PX_PER_MM = 96 / 25.4;

const A4_PORTRAIT_MM = { w: 210, h: 297 } as const;

export type A4TiledPdfLineRegionMm = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type CreateRealSizeA4TiledPdfOptions = {
  lineRegionsMm?: A4TiledPdfLineRegionMm[];
};

function a4PageSizeMm(landscape: boolean): { pageW: number; pageH: number } {
  if (landscape) {
    return { pageW: A4_PORTRAIT_MM.h, pageH: A4_PORTRAIT_MM.w };
  }
  return { pageW: A4_PORTRAIT_MM.w, pageH: A4_PORTRAIT_MM.h };
}

/** Axis-aligned overlap in mm (small epsilon for float noise). */
function rectIntersectsMM(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
  eps = 0.25
): boolean {
  return !(ax + aw <= bx + eps || ax + eps >= bx + bw || ay + ah <= by + eps || ay + eps >= by + bh);
}

function tileIntersectsAnyRegion(
  tileX: number,
  tileY: number,
  tileW: number,
  tileH: number,
  regions: A4TiledPdfLineRegionMm[]
): boolean {
  for (const r of regions) {
    if (rectIntersectsMM(tileX, tileY, tileW, tileH, r.x, r.y, r.w, r.h)) {
      return true;
    }
  }
  return false;
}

async function drawTile(
  pdfDoc: jsPDF,
  svgTemplate: SVGSVGElement,
  container: HTMLDivElement,
  xMin: number,
  yMin: number,
  pageW: number,
  pageH: number
): Promise<void> {
  const tileSvg = svgTemplate.cloneNode(true) as SVGSVGElement;
  tileSvg.setAttribute('viewBox', `${xMin} ${yMin} ${pageW} ${pageH}`);
  tileSvg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
  tileSvg.setAttribute('width', String(Math.round(pageW * PX_PER_MM)));
  tileSvg.setAttribute('height', String(Math.round(pageH * PX_PER_MM)));

  container.appendChild(tileSvg);
  try {
    await pdfDoc.svg(tileSvg, {
      x: 0,
      y: 0,
      width: pageW,
      height: pageH,
    });
  } finally {
    if (tileSvg.parentNode === container) {
      container.removeChild(tileSvg);
    }
  }
}

async function buildTiledPdf(
  svgElement: SVGSVGElement,
  widthMm: number,
  heightMm: number,
  regions: A4TiledPdfLineRegionMm[] | undefined,
  cullEmptyTiles: boolean
): Promise<Blob> {
  const landscape = widthMm > heightMm;
  const { pageW, pageH } = a4PageSizeMm(landscape);
  const cols = Math.max(1, Math.ceil(widthMm / pageW));
  const rows = Math.max(1, Math.ceil(heightMm / pageH));

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.visibility = 'hidden';
  document.body.appendChild(container);

  const tryEmit = async (skipEmpty: boolean): Promise<{ pdf: jsPDF; pages: number } | null> => {
    let pdfDoc: jsPDF | null = null;
    let pages = 0;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const xMin = col * pageW;
        const yMin = row * pageH;
        if (
          skipEmpty &&
          regions &&
          regions.length > 0 &&
          !tileIntersectsAnyRegion(xMin, yMin, pageW, pageH, regions)
        ) {
          continue;
        }
        if (!pdfDoc) {
          pdfDoc = new jsPDF({
            unit: 'mm',
            format: 'a4',
            orientation: landscape ? 'landscape' : 'portrait',
            hotfixes: ['px_scaling'],
          });
        } else {
          pdfDoc.addPage('a4', landscape ? 'landscape' : 'portrait');
        }
        await drawTile(pdfDoc, svgElement, container, xMin, yMin, pageW, pageH);
        pages++;
      }
    }
    return pdfDoc ? { pdf: pdfDoc, pages } : null;
  };

  try {
    let result = await tryEmit(cullEmptyTiles);
    if (!result || result.pages === 0) {
      result = await tryEmit(false);
    }
    if (!result) {
      throw new Error('PDF tiling produced no pages');
    }
    return result.pdf.output('blob');
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Multi-page DIN A4 at real scale. Uses one grid over the full document; each tile is drawn at most once.
 * With `lineRegionsMm`, drops tiles that do not intersect any line (avoids huge blank areas between groups).
 */
export async function createRealSizeA4TiledPdf(
  svgString: string,
  widthMm: number,
  heightMm: number,
  options?: CreateRealSizeA4TiledPdfOptions
): Promise<Blob> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgElement = doc.documentElement;

  if (!(svgElement instanceof SVGSVGElement)) {
    throw new Error('Invalid or empty SVG');
  }

  const regions = options?.lineRegionsMm;
  const cull = !!(regions && regions.length > 0);

  return buildTiledPdf(svgElement, widthMm, heightMm, regions, cull);
}

/**
 * Returns a single-page PDF as a Blob. Page size = widthMm × heightMm (no A4 split).
 */
export async function createRealSizePdf(svgString: string, widthMm: number, heightMm: number): Promise<Blob> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgElement = doc.documentElement;

  if (!(svgElement instanceof SVGSVGElement)) {
    throw new Error('Invalid or empty SVG');
  }

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.visibility = 'hidden';
  document.body.appendChild(container);
  container.appendChild(svgElement);

  try {
    const pdfDoc = new jsPDF({
      unit: 'mm',
      format: [widthMm, heightMm],
      hotfixes: ['px_scaling'],
    });

    await pdfDoc.svg(svgElement, {
      x: 0,
      y: 0,
      width: widthMm,
      height: heightMm,
    });

    return pdfDoc.output('blob');
  } finally {
    document.body.removeChild(container);
  }
}
